import { useMemo, useCallback } from 'react';
import HandsOnTable from 'handsontable';
import { buildColumnsWithRenderers } from '../config/columns';
import { createTrackingDateRenderer, createStyledCellRenderer, createRowHeaderRenderer } from '../renderers';
import { getBaseTableSettings, getContextMenuConfig } from '../config/handsontable';
import { useShipmentMutations } from '../providers/ShipmentMutationsProvider';
import { useTableUI } from '../providers/TableUIProvider';
import { Shipment } from '../types';
import { createISODateEditor } from '../config/handsontable';

interface UseTableSettingsParams {
  fields: any[] | undefined;
  tableData: any[];
  shipments: any[];
  shipmentsMap: Record<string, Shipment>;
  trackingJobsMap: Record<string, any>;
  hotRef: any;
  projectId: string;
  userId: string;
}

export const useTableSettings = ({
  fields,
  tableData,
  shipments,
  shipmentsMap,
  trackingJobsMap,
  hotRef,
  projectId,
  userId,
}: UseTableSettingsParams) => {
  const { handleAfterChange, handleAfterPaste } = useShipmentMutations();
  const { setSelectedRow, setSidebarOpened, openDeleteConfirmModal } = useTableUI();

  // Renderers
  const trackingDateRenderer = useCallback(createTrackingDateRenderer({ shipmentsMap }), [shipmentsMap]);

  const styledCellRenderer = useCallback(createStyledCellRenderer({ shipmentsMap }), [shipmentsMap]);

  const rowHeaderRenderer = useCallback(createRowHeaderRenderer({ trackingJobsMap }), [shipments, trackingJobsMap]);

  const ISODateEditorWithContext = useMemo(
    () => createISODateEditor({ instance: hotRef.current?.hotInstance, shipmentsMap }),
    [hotRef, shipmentsMap],
  );

  // Columns
  const columns = useMemo(() => {
    if (!fields?.length) return [];
    return buildColumnsWithRenderers(fields, trackingDateRenderer, styledCellRenderer, ISODateEditorWithContext);
  }, [fields, trackingDateRenderer, styledCellRenderer, ISODateEditorWithContext]);

  // Delete handler
  const handleDeleteShipment = useCallback(
    (shipmentId: string, reference: string) => {
      openDeleteConfirmModal(shipmentId, reference);
    },
    [openDeleteConfirmModal],
  );

  // Context menu
  const contextMenu = useMemo(
    () => getContextMenuConfig(setSelectedRow, setSidebarOpened, handleDeleteShipment, shipments, projectId),
    [setSelectedRow, setSidebarOpened, handleDeleteShipment, shipments, projectId],
  );

  // Wrapped handlers
  const wrappedHandleAfterChange = useCallback(
    (changes: HandsOnTable.CellChange[] | null, source: string) => {
      if (!hotRef.current?.hotInstance || !projectId || !userId) return;
      handleAfterChange({
        changes,
        source,
        hotInstance: hotRef.current.hotInstance,
        projectId,
        userId,
      });
    },
    [handleAfterChange, hotRef, projectId, userId],
  );

  const wrappedHandleAfterPaste = useCallback(
    (data: any[][], coords: any[]) => {
      if (!hotRef.current?.hotInstance || !projectId || !userId) return;
      handleAfterPaste({
        data,
        coords,
        hotInstance: hotRef.current.hotInstance,
        columns,
        projectId,
        userId,
      });
    },
    [handleAfterPaste, hotRef, columns, projectId, userId],
  );

  // Settings
  const settings = useMemo(() => {
    if (!columns.length) return null;

    return {
      ...getBaseTableSettings(),
      data: tableData,
      columns,
      multiColumnSorting: true,
      dropdownMenu: ['filter_by_condition', 'filter_by_value', 'filter_action_bar'] as any,
      filters: true,
      contextMenu,
      afterChange: wrappedHandleAfterChange,
      afterPaste: wrappedHandleAfterPaste,
      rowHeaders: true,
      afterGetRowHeader: rowHeaderRenderer,
    };
  }, [columns, tableData, contextMenu, wrappedHandleAfterChange, wrappedHandleAfterPaste, rowHeaderRenderer]);

  return { settings, columns };
};
